import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import ReportActionService from '../../../shared/services/report-action/report-action.service'
import { IReportAction } from '../../../shared/services/report-action/report-action.model'
import { actionEntityName, friendlyActionTitle } from '../../../shared/services/report-action/report-action-title'

// Dashboard companion tile for data owners: their outstanding data tasks at a glance,
// with the next few due, linking through to the full My Tasks list. Follows the
// dashboard's entity filter: locationId narrows the tasks to that site's actions.
export const DataOwnerTasksCard: FunctionComponent<{ locationId?: string }> = ({ locationId }) => {
  const navigate = useNavigate()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userDetails = useContext(UserDetailsContext).userDetails

  const [outstandingCount, setOutstandingCount] = useState<number | null>(null)
  const [nextTasks, setNextTasks] = useState<IReportAction[]>([])

  const reportPeriodId = reportPeriodContext.lastSelectedOpenReportId

  useEffect(() => {
    if (!reportPeriodId || !userDetails?.id) return
    new ReportActionService()
      .getReportActionsWithPagination(reportPeriodId, `&userId=${userDetails.id}&status=outstanding${locationId ? `&locationId=${locationId}` : ''}`, 0, 3)
      .then(response => {
        setNextTasks(response.rows)
        setOutstandingCount(response.count)
      })
      .catch(() => { setNextTasks([]); setOutstandingCount(null) })
  }, [reportPeriodId, userDetails?.id, locationId])

  if (outstandingCount === null) return null

  return (
    <Paper
      variant='outlined'
      onClick={() => navigate('/my-tasks')}
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
      }}>
      <Stack direction='row' alignItems='center'>
        <Typography variant='subtitle1' sx={{ flex: 1 }}><b>My Tasks</b></Typography>
        <Button variant='text' size='small' onClick={(e) => { e.stopPropagation(); navigate('/my-tasks') }} sx={{ textTransform: 'none' }}>
          View all
        </Button>
      </Stack>
      {outstandingCount === 0
        ? <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 2 }}>
          <CheckCircleOutlineIcon color='success' />
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            You're all caught up. No outstanding data tasks.
          </Typography>
        </Stack>
        : <>
          <Stack direction='row' spacing={1} sx={{ alignItems: 'baseline' }}>
            <Typography variant='h4'>{outstandingCount}</Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              outstanding {outstandingCount === 1 ? 'task' : 'tasks'}
            </Typography>
          </Stack>
          <Box sx={{ mt: 1.5 }}>
            {nextTasks.map(task => (
              <Stack
                key={task.id}
                direction='row'
                spacing={1}
                alignItems='baseline'
                onClick={(e) => { e.stopPropagation(); navigate(`/emissions/overview/group/${task.groupId}?actionId=${task.id}`) }}
                sx={{ py: 0.5, cursor: 'pointer', '&:hover .task-title': { textDecoration: 'underline' } }}>
                <Typography className='task-title' variant='body2' noWrap sx={{ flex: 1 }}>{friendlyActionTitle(task)}</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary', flexShrink: 0 }}>
                  {actionEntityName(task) ? `${actionEntityName(task)} · ` : ''}
                  {moment(task.endDate).format('D MMM')}
                </Typography>
              </Stack>
            ))}
          </Box>
        </>
      }
    </Paper>
  )
}
